import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Compass, Check, ArrowRight, ArrowDown, Info, Gauge, BookOpen, HelpCircle, CheckCircle2, RotateCcw, FileCheck2 } from "lucide-react";
import { FadeUp, Overline, EASE } from "./Reveal";
import { scrollToId } from "./Navbar";

const COACH_FEATURES = [
  "Test de niveau", "Apprentissage personnalisé", "Explications simplifiées", "Quiz",
  "Questions ciblées", "Révision des erreurs", "Suivi de progression", "Examens blancs",
];

const DEMARCHES_EXAMPLES = [
  "« Mon titre de séjour arrive bientôt à expiration. Que dois-je vérifier ? »",
  "« Je souhaite préparer une demande de naturalisation. Par où commencer ? »",
  "« Je viens de changer d'adresse. Quelle démarche dois-je effectuer ? »",
];

const DEMARCHES_FEATURES = [
  "Comprendre votre question", "Demander les informations nécessaires", "Expliquer les étapes",
  "Indiquer les documents potentiellement nécessaires", "Orienter vers l'organisme compétent",
  "Fournir les liens officiels lorsque disponibles",
];

export function Agents() {
  return (
    <section id="coach" className="bg-slate-50/70 py-20 sm:py-28">
      <div className="container-integra">
        <FadeUp className="max-w-2xl">
          <Overline>Les deux agents IA</Overline>
          <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Deux assistants IA. Un seul objectif : <span className="text-bleu">vous aider à avancer.</span>
          </h2>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">
            Pas besoin de connaître les termes administratifs. Posez simplement votre question.
          </p>
        </FadeUp>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <FadeUp className="h-full">
            <div className="card-integra card-hover flex h-full flex-col overflow-hidden" data-testid="agent-card-coach-civique">
              <div className="bg-gradient-to-br from-bleu to-bleu-dark p-7 text-white">
                <span className="inline-flex rounded-2xl bg-white/15 p-3 backdrop-blur"><Bot className="h-7 w-7" /></span>
                <h3 className="mt-4 font-display text-2xl font-extrabold">Coach Civique IA</h3>
                <p className="mt-1 text-sm text-white/80">Votre coach personnel pour apprendre et vous préparer.</p>
              </div>
              <div className="flex flex-1 flex-col p-7">
                <div className="grid grid-cols-2 gap-2.5">
                  {COACH_FEATURES.map((f) => (
                    <span key={f} className="flex items-center gap-2 text-[13px] font-medium text-slate-600">
                      <Check className="h-4 w-4 shrink-0 text-emeraude" /> {f}
                    </span>
                  ))}
                </div>
                <div className="mt-6 rounded-2xl bg-slate-50 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Exemple de conversation</p>
                  <p className="mt-2.5 w-fit rounded-xl rounded-br-sm bg-bleu px-3.5 py-2.5 text-xs font-medium text-white">
                    « Je ne comprends pas bien le rôle du Parlement. »
                  </p>
                  <p className="mt-2 w-fit rounded-xl rounded-tl-sm border border-slate-100 bg-white px-3.5 py-2.5 text-xs leading-relaxed text-slate-600 shadow-sm">
                    « Je vais vous l'expliquer simplement, puis vous proposer quelques questions pour vérifier que vous avez compris. »
                  </p>
                </div>
                <a href="#examen" onClick={scrollToId("examen")} className="btn-primary mt-6 w-fit" data-testid="agent-coach-cta">
                  Découvrir le Coach civique <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.12} className="h-full" >
            <div id="assistant" className="card-integra card-hover flex h-full flex-col overflow-hidden" data-testid="agent-card-assistant-demarches">
              <div className="bg-gradient-to-br from-emeraude to-emerald-700 p-7 text-white">
                <span className="inline-flex rounded-2xl bg-white/15 p-3 backdrop-blur"><Compass className="h-7 w-7" /></span>
                <h3 className="mt-4 font-display text-2xl font-extrabold">Assistant Démarches IA</h3>
                <p className="mt-1 text-sm text-white/80">Un assistant pour vous aider à comprendre vos démarches.</p>
              </div>
              <div className="flex flex-1 flex-col p-7">
                <div className="space-y-2">
                  {DEMARCHES_EXAMPLES.map((q) => (
                    <p key={q} className="rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2.5 text-xs italic leading-relaxed text-slate-600">{q}</p>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {DEMARCHES_FEATURES.map((f) => (
                    <span key={f} className="flex items-start gap-2 text-[13px] font-medium text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emeraude" /> {f}
                    </span>
                  ))}
                </div>
                <a href="#question" onClick={scrollToId("question")} className="btn-primary mt-6 w-fit !bg-emeraude hover:!bg-emerald-700" data-testid="agent-demarches-cta">
                  Découvrir l'assistant démarches <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-bleu/15 bg-bleu-light/60 px-5 py-4" data-testid="agents-disclaimer">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-bleu" />
            <p className="text-sm leading-relaxed text-slate-600">
              <strong className="text-slate-800">Les réponses administratives sont accompagnées de sources officielles</strong> et
              ne remplacent pas les informations délivrées par les administrations compétentes.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

const FLOW = [
  { icon: Gauge, label: "Test de niveau" },
  { icon: BookOpen, label: "Cours" },
  { icon: HelpCircle, label: "Quiz" },
  { icon: CheckCircle2, label: "Correction" },
  { icon: RotateCcw, label: "Révision personnalisée" },
  { icon: FileCheck2, label: "Examen blanc" },
];

const BARS = [
  { label: "Principes et valeurs", value: 82, color: "bg-bleu" },
  { label: "Institutions", value: 65, color: "bg-bleu-accent" },
  { label: "Droits et devoirs", value: 76, color: "bg-violet-500" },
  { label: "Histoire, géographie et culture", value: 81, color: "bg-emeraude" },
  { label: "Vie en société", value: 70, color: "bg-ambre" },
];

function Donut({ value }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-36 w-36">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E2E8F0" strokeWidth="11" />
        <motion.circle
          cx="60" cy="60" r={r} fill="none" stroke="#1E3FAE" strokeWidth="11" strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: c * (1 - value / 100) }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-extrabold text-slate-900">{value} %</span>
        <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Global</span>
      </div>
    </div>
  );
}

function QuizDemo() {
  const [picked, setPicked] = useState(null);
  const options = [
    { text: "Liberté, Égalité, Fraternité", correct: true },
    { text: "Travail, Famille, Patrie", correct: false },
    { text: "Unité, Progrès, Justice", correct: false },
  ];
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5" data-testid="quiz-demo">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Question d'exemple — touchez une réponse</p>
      <p className="mt-2 text-sm font-semibold text-slate-900">Quelle est la devise de la République française ?</p>
      <div className="mt-3 space-y-2">
        {options.map((o, i) => {
          const state = picked === null ? "idle" : o.correct ? "correct" : picked === i ? "wrong" : "idle";
          return (
            <button
              key={o.text}
              data-testid={`quiz-demo-option-${i}`}
              onClick={() => setPicked(i)}
              className={`w-full rounded-xl border px-4 py-2.5 text-left text-[13px] font-medium transition-all duration-300 ${
                state === "correct"
                  ? "border-emeraude bg-emerald-50 text-emerald-900"
                  : state === "wrong"
                    ? "border-rouge bg-rose-50 text-rose-900"
                    : "border-slate-200 text-slate-600 hover:border-bleu/40 hover:bg-bleu-light/50"
              }`}
            >
              {o.text}
              {state === "correct" && " ✓"}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <p className="mt-3 text-xs font-medium text-slate-500">
          {options[picked].correct
            ? "Bonne réponse ! Le Coach civique vous explique chaque notion simplement."
            : "Presque ! Le Coach civique vous réexplique et vous propose de réviser ce thème."}
        </p>
      )}
    </div>
  );
}

export function Exam() {
  return (
    <section id="examen" className="py-20 sm:py-28">
      <div className="container-integra">
        <div className="grid items-start gap-14 lg:grid-cols-2">
          <FadeUp>
            <Overline>Examen civique</Overline>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Préparez-vous à votre examen civique <span className="text-bleu">avec méthode.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Depuis le 1er janvier 2026, l'examen civique officiel compte 40 questions en 45 minutes maximum, avec un
              seuil de réussite de 32/40. Évaluez votre niveau, révisez les 5 thématiques officielles et entraînez-vous
              dans des conditions proches de l'examen réel.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {["40 questions", "45 minutes max", "Seuil : 32/40", "28 connaissances + 12 mises en situation", "5 thématiques officielles"].map((c) => (
                <span key={c} className="rounded-full bg-bleu-light px-3.5 py-1.5 text-[11px] font-bold text-bleu" data-testid="exam-official-chip">{c}</span>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {FLOW.map((s, i) => (
                <FadeUp key={s.label} delay={i * 0.08}>
                  <div className="flex items-center gap-2.5">
                    <div className="card-integra flex flex-1 flex-col items-center gap-2 px-2 py-4 text-center">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-bleu-light">
                        <s.icon className="h-4.5 w-4.5 text-bleu" />
                      </span>
                      <p className="text-[11px] font-semibold leading-tight text-slate-700">{s.label}</p>
                    </div>
                    {i < FLOW.length - 1 && i % 3 !== 2 && <ArrowRight className="hidden h-4 w-4 shrink-0 text-slate-300 sm:block" />}
                  </div>
                </FadeUp>
              ))}
            </div>

            <div className="mt-8">
              <QuizDemo />
            </div>

            <Link to="/signup" className="btn-primary mt-8" data-testid="exam-cta">
              Découvrir la préparation <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="card-integra p-7" data-testid="exam-progress-card">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-slate-900">Votre progression</h3>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-ambre">
                  Exemple visuel
                </span>
              </div>
              <div className="mt-6 flex flex-col items-center gap-8 sm:flex-row">
                <Donut value={72} />
                <div className="w-full flex-1 space-y-4">
                  {BARS.map((b, i) => (
                    <div key={b.label}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-600">{b.label}</span>
                        <span className="font-bold text-slate-900">{b.value} %</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                        <motion.div
                          className={`h-full rounded-full ${b.color}`}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${b.value}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, delay: 0.2 + i * 0.12, ease: EASE }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="mt-6 flex items-start gap-2 rounded-xl bg-slate-50 px-4 py-3 text-[11px] leading-relaxed text-slate-400">
                <ArrowDown className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Ces données sont uniquement des exemples visuels et ne représentent pas les résultats d'un utilisateur réel.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
